import React, { FC, useContext, useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AppContext } from '../../containers/appContextContainer';
import { useConfig } from '../../default-config';
import { EiendomTypeWeb, EiendomWeb, SoeknadstypeWeb, ValgtWeb } from '../../generated-types';
import { useEiendomverdi, useUpdateEiendomsverdi } from '../../hooks/useEiendom';
import useFeatureToggle from '../../hooks/useFeatureToggle';
import { useTranslation } from '../../translations';
import ETakstFormData from '../eiendom/eTakstFormData';
import ETakstSoek from '../eiendom/eTakstSoek';
import EtakstValg from '../eiendom/eTakstValg';
import Verdivurdering, { OenskerVerdivurdering } from '../eiendom/verdivurdering';
import Nedbetalingstid, { NedbetalingstidFormData } from '../laan/nedbetalingstid';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import BoligverdiInfoBox from './boligverdiInfoBox';
import BoligverdiLaanebeloep, { BoligverdiLaanebeloepFormData } from './boligverdiLaanebeloep';
import BoligverdiLaaneformaal, { BoligverdiLaaneformaalFormData } from './boligverdiLaaneformaal';

export type BoligverdiFormData = ETakstFormData &
    BoligverdiLaanebeloepFormData &
    NedbetalingstidFormData &
    OenskerVerdivurdering &
    BoligverdiLaaneformaalFormData;

interface Props {
    navigateTo: string;
    navigateToOption: string;
    navigateFrom: string;
}

const Boligverdi: FC<Props> = ({ navigateTo, navigateToOption, navigateFrom }) => {
    const navigate = useNavigate();
    const { boligVerdi } = useTranslation();
    const { data: featureTogglesUtvideDatagrunnlag } = useFeatureToggle();
    const { utvideDatagrunnlag } = featureTogglesUtvideDatagrunnlag || {};
    const { soeknadstype, setSoeknadstype } = useContext(AppContext);
    const { featureToggles } = useConfig();
    const { data: eiendomsverdi } = useEiendomverdi(soeknadstype, EiendomTypeWeb.CURRENT);
    const { mutate: updateEiendomverdi, isPending: isUpdating } = useUpdateEiendomsverdi(eiendomsverdi, () => {
        if (eiendomsverdi?.soeknadstype === SoeknadstypeWeb.NYTT_BOLIGLAAN || eiendomsverdi?.annenBolig) {
            navigate(navigateToOption);
        } else {
            navigate(navigateTo);
        }
    });

    const methods = useForm<BoligverdiFormData>({
        defaultValues: {
            harETakst: ValgtWeb.IKKE_VALGT,
            eTakst: {
                verdi: 0,
                fellesgjeld: null,
                aktivertDato: null,
                referansenummer: '',
            },
            soektBeloep: undefined,
            nedbetalingstid: 30,
            oenskerVerdivurdering: eiendomsverdi?.eiendom?.oenskerVerdivurdering || ValgtWeb.IKKE_VALGT,
            samtykke: eiendomsverdi?.eiendom?.oenskerVerdivurdering === ValgtWeb.JA,
        },
    });
    const { handleSubmit, control, reset } = methods;
    const harETakst = useWatch({ name: 'harETakst', control });

    useEffect(() => {
        if (eiendomsverdi?.soeknadstype) {
            setSoeknadstype(eiendomsverdi.soeknadstype);
        }
    }, [eiendomsverdi?.soeknadstype, setSoeknadstype]);

    useEffect((): void => {
        if (eiendomsverdi?.eiendom?.etakstWeb?.referansenummer) {
            reset({
                harETakst: eiendomsverdi.harEtakst,
                eTakst: eiendomsverdi?.eiendom.etakstWeb,
                soektBeloep: eiendomsverdi.soektBeloep || undefined,
                nedbetalingstid: eiendomsverdi.nedbetalingstid ? eiendomsverdi.nedbetalingstid / 12 : 30,
            });
        } else {
            reset({
                harETakst: eiendomsverdi?.harEtakst,
                soektBeloep: eiendomsverdi?.soektBeloep || undefined,
                nedbetalingstid: eiendomsverdi?.nedbetalingstid ? eiendomsverdi.nedbetalingstid / 12 : 30,
                laaneformaal: eiendomsverdi?.laaneformaal || [],
                annetLaaneformaal: eiendomsverdi?.annetLaaneformaal || '',
            });
        }
    }, [eiendomsverdi, reset]);

    const onSubmitFlyttLaanBoligverdi = (data: BoligverdiFormData) => {
        if (eiendomsverdi?.eiendom) {
            const updatedEiendom: EiendomWeb = {
                ...eiendomsverdi.eiendom,
            };
            updatedEiendom.oenskerVerdivurdering = data.oenskerVerdivurdering;
            updatedEiendom.etakstWeb = data.eTakst || null;
            updatedEiendom.eiendomsverdiWeb = eiendomsverdi.eiendom.eiendomsverdiWeb;
            updateEiendomverdi({
                soeknadId: eiendomsverdi.soeknadId,
                eiendom: updatedEiendom,
                harEtakst: data.harETakst,
                harBolig: ValgtWeb.IKKE_VALGT,
                harPantIBolig: ValgtWeb.IKKE_VALGT,
                eksisterendeLaanebeloep: eiendomsverdi.eksisterendeLaanebeloep || null,
                soektBeloep: data.soektBeloep || null,
                nedbetalingstid: data.nedbetalingstid * 12 || null,
                laaneformaal: data.laaneformaal || [],
                annetLaaneformaal: data.annetLaaneformaal || null,
            });
        }
    };

    return (
        <div>
            <PageCard
                title={
                    eiendomsverdi?.annenBolig || eiendomsverdi?.soeknadstype === SoeknadstypeWeb.NYTT_BOLIGLAAN
                        ? boligVerdi.titleLaanbeloep
                        : boligVerdi.title
                }
            >
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmitFlyttLaanBoligverdi)} className="row row-gap-large">
                        <EtakstValg />
                        {harETakst === ValgtWeb.JA && <ETakstSoek />}
                        <BoligverdiInfoBox eiendomsverdi={eiendomsverdi} />
                        {featureToggles.aktivsamarbeid && <Verdivurdering />}
                        {(eiendomsverdi?.annenBolig ||
                            eiendomsverdi?.soeknadstype === SoeknadstypeWeb.NYTT_BOLIGLAAN) && (
                            <>
                                <BoligverdiLaanebeloep
                                    label={
                                        eiendomsverdi?.annenBolig
                                            ? boligVerdi.labelOekelaan
                                            : boligVerdi.labelLaanebeloep
                                    }
                                />
                                {utvideDatagrunnlag && <BoligverdiLaaneformaal />}
                                <Nedbetalingstid />
                            </>
                        )}
                        <PageFooter
                            soeknadId={eiendomsverdi?.soeknadId}
                            onForrigeClick={() => navigate(navigateFrom)}
                            showSpinner={isUpdating}
                        />
                    </form>
                </FormProvider>
            </PageCard>
        </div>
    );
};

export default Boligverdi;
